import {
  applyPlatformTheme,
  normalizeTheme,
  getThemeKey,
  createDraftTheme,
  getDefaultThemeName,
  getBuiltinThemes,
  type PlatformTheme,
  type ThemePreference,
} from '@platzona/platform-theme';
import { apiFetch } from '../utils/api';
import { authService } from './authService';

const STORAGE_KEY = 'saas:platform-theme-key';
const STORAGE_NAME_KEY = 'saas:platform-theme-name';
const THEME_EVENT = 'saas-platform-theme-change';

let cachedThemes: PlatformTheme[] | null = null;
let activeTheme: PlatformTheme | null = null;

export type { PlatformTheme, ThemePreference };

export function getActiveTheme(): PlatformTheme | null {
  return activeTheme;
}

export function onPlatformThemeChange(handler: (theme: PlatformTheme) => void): () => void {
  const fn = (e: Event) => handler((e as CustomEvent<PlatformTheme>).detail);
  window.addEventListener(THEME_EVENT, fn);
  return () => window.removeEventListener(THEME_EVENT, fn);
}

function persistLocal(theme: PlatformTheme): void {
  try {
    localStorage.setItem(STORAGE_KEY, getThemeKey(theme));
    localStorage.setItem(STORAGE_NAME_KEY, theme.name);
  } catch { /* ignore */ }
}

export function applyTheme(theme: PlatformTheme): void {
  const normalized = normalizeTheme(theme);
  activeTheme = normalized;
  applyPlatformTheme(normalized);
  persistLocal(normalized);
  window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: normalized }));
}

export async function fetchDefaultThemes(): Promise<PlatformTheme[]> {
  if (cachedThemes) return cachedThemes;
  const fallback = getBuiltinThemes().map(normalizeTheme);
  try {
    const data = await apiFetch<{ themes: PlatformTheme[] }>('/api/saas/v1/themes/defaults');
    cachedThemes = (data.themes?.length ? data.themes : fallback).map(normalizeTheme);
  } catch {
    cachedThemes = fallback;
  }
  return cachedThemes;
}

async function resolveFromKey(key: string, builtins: PlatformTheme[]): Promise<PlatformTheme | null> {
  if (key.startsWith('custom:')) {
    const id = key.slice(7);
    try {
      return normalizeTheme(await apiFetch<PlatformTheme>(`/api/saas/v1/themes/${id}`));
    } catch { return null; }
  }
  const name = key.startsWith('builtin:') ? key.slice(8) : key;
  return builtins.find(t => t.name === name) ?? null;
}

export async function loadAndApplyTheme(): Promise<PlatformTheme> {
  const builtins = await fetchDefaultThemes();
  const fallback = builtins.find(t => t.name === getDefaultThemeName('saas')) ?? builtins[0];

  if (authService.isAuthenticated()) {
    try {
      const data = await apiFetch<{ theme: PlatformTheme }>('/api/saas/v1/themes/settings');
      if (data.theme) {
        applyTheme(data.theme);
        return data.theme;
      }
    } catch { /* fall through */ }
  }

  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    const resolved = await resolveFromKey(stored, builtins);
    if (resolved) { applyTheme(resolved); return resolved; }
  }

  if (fallback) { applyTheme(fallback); return fallback; }
  const draft = createDraftTheme(getDefaultThemeName('saas'));
  applyTheme(draft);
  return draft;
}

async function persistThemePreference(theme: PlatformTheme): Promise<void> {
  if (!authService.isAuthenticated()) return;
  const body = theme._id
    ? { kind: 'custom' as const, themeId: theme._id }
    : { kind: 'builtin' as const, builtinName: theme.name };
  await apiFetch('/api/saas/v1/themes/settings', { method: 'PUT', body: JSON.stringify(body) });
}

export async function selectTheme(theme: PlatformTheme): Promise<void> {
  applyTheme(theme);
  try {
    await persistThemePreference(theme);
  } catch {
    /* keep local apply; account sync retried on next auth/load */
  }
}
